import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Home, Briefcase, MapPin, Plus, Loader2, Smartphone, CreditCard, Wallet, Banknote, QrCode } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr, apiError } from "@/lib/api";
import { BillDetails } from "@/pages/customer/Cart";

const PAY = [
  { k: "phonepe", l: "PhonePe UPI", icon: Smartphone },
  { k: "gpay", l: "Google Pay UPI", icon: Smartphone },
  { k: "upi", l: "Other UPI / QR", icon: QrCode },
  { k: "card", l: "Credit / Debit Card", icon: CreditCard },
  { k: "cod", l: "Cash on Delivery", icon: Banknote },
];

export default function Checkout() {
  const nav = useNavigate();
  const { cart, count, refresh } = useCart();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [addrId, setAddrId] = useState(null);
  const [pay, setPay] = useState("cod");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "Home", line1: "", city: "Hyderabad", pincode: "" });

  useEffect(() => {
    if (!count) { nav("/"); return; }
    api.get("/addresses").then((r) => { setAddresses(r.data); setAddrId(r.data[0]?.id || null); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!cart) return null;

  const addAddress = async () => {
    if (!form.line1) return toast.error("Enter address");
    try { const { data } = await api.post("/addresses", form); setAddresses((a) => [...a, data]); setAddrId(data.id); setAdding(false); toast.success("Address added"); } catch (e) { toast.error(apiError(e)); }
  };

  const loadScript = (src) => new Promise((res) => {
    if (document.querySelector(`script[src="${src}"]`)) return res(true);
    const s = document.createElement("script"); s.src = src; s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
  });

  const openRazorpay = (pd, order) => new Promise(async (resolve, reject) => {
    const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!ok || !window.Razorpay) { toast.error("Couldn't load payment gateway"); return reject(new Error("script")); }
    const rzp = new window.Razorpay({
      key: pd.key_id, amount: pd.amount, currency: pd.currency, name: "BiteGoFood",
      description: `Order ${order.code}`, order_id: pd.razorpay_order_id,
      prefill: { name: user?.name, contact: user?.phone, email: user?.email || "" },
      theme: { color: "#FF6B35" },
      handler: async (r) => {
        try { await api.post("/payments/verify", { order_id: order.id, razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature }); toast.success("Payment verified"); resolve(); }
        catch (e) { toast.error("Payment verification failed"); reject(e); }
      },
      modal: { ondismiss: () => reject(new Error("dismissed")) },
    });
    rzp.on("payment.failed", () => { toast.error("Payment failed. Try again or change method."); reject(new Error("failed")); });
    rzp.open();
  });

  const placeOrder = async () => {
    if (!addrId) return toast.error("Select a delivery address");
    setBusy(true);
    let trainCtx = null, cinemaCtx = null;
    try { trainCtx = JSON.parse(localStorage.getItem("bitego_train_ctx") || "null"); } catch { trainCtx = null; }
    try { cinemaCtx = JSON.parse(localStorage.getItem("bitego_cinema_ctx") || "null"); } catch { cinemaCtx = null; }
    try {
      const { data: order } = await api.post("/orders", {
        from_cart: true, address_id: addrId,
        payment_method: pay === "cod" ? "cod" : (pay === "card" ? "card" : "upi"),
        train_ctx: trainCtx, cinema_ctx: cinemaCtx,
      });
      if (pay !== "cod") {
        const { data: pd } = await api.post("/payments/create", { order_id: order.id });
        if (pd.gateway === "razorpay") await openRazorpay(pd, order);
        else { await api.post("/payments/verify", { order_id: order.id, success: true }); toast.success("Payment verified (demo mode)"); }
      } else {
        toast.success("Order placed · Cash on Delivery");
      }
      localStorage.removeItem("bitego_train_ctx");
      localStorage.removeItem("bitego_cinema_ctx");
      await refresh();
      nav(`/order/${order.id}`);
    } catch (e) {
      // Payment failed or cancelled — keep the cart intact for retry
      await refresh();
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-40">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg">Checkout</h1>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3 flex items-center gap-2"><MapPin size={17} className="text-coral" /> Delivery address</h3>
          <div className="space-y-2">
            {addresses.map((a) => (
              <label key={a.id} className={`flex items-start gap-3 rounded-xl p-3 border cursor-pointer ${addrId === a.id ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`address-${a.id}`}>
                <input type="radio" checked={addrId === a.id} onChange={() => setAddrId(a.id)} className="accent-coral mt-1 w-4 h-4" />
                {a.label === "Work" ? <Briefcase size={16} className="mt-1 text-ink-600" /> : <Home size={16} className="mt-1 text-ink-600" />}
                <div className="min-w-0"><div className="font-bold text-sm">{a.label}</div><div className="text-xs text-ink-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city} {a.pincode}</div></div>
              </label>
            ))}
          </div>
          {adding ? (
            <div className="mt-3 space-y-2 bg-surface rounded-xl p-3">
              <div className="flex gap-2">{["Home", "Work", "Other"].map((l) => <button key={l} onClick={() => setForm((f) => ({ ...f, label: l }))} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${form.label === l ? "bg-coral text-white border-coral" : "bg-white border-border"}`}>{l}</button>)}</div>
              <input value={form.line1} onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))} placeholder="Flat / House, Street, Area" className="w-full bg-white rounded-lg px-3 py-2 text-sm outline-none border border-border" data-testid="new-address-line" />
              <div className="flex gap-2"><input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="City" className="flex-1 bg-white rounded-lg px-3 py-2 text-sm border border-border" /><input value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))} placeholder="Pincode" className="w-28 bg-white rounded-lg px-3 py-2 text-sm border border-border" /></div>
              <div className="flex gap-2"><button onClick={addAddress} className="flex-1 bg-coral text-white font-bold py-2 rounded-lg text-sm" data-testid="save-address">Save</button><button onClick={() => setAdding(false)} className="px-4 bg-white border border-border rounded-lg text-sm">Cancel</button></div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} className="mt-3 text-sm text-coral font-semibold flex items-center gap-1" data-testid="add-address-btn"><Plus size={15} /> Add new address</button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3 flex items-center gap-2"><Wallet size={17} className="text-coral" /> Payment method</h3>
          <div className="space-y-2">
            {PAY.map((p) => (
              <label key={p.k} className={`flex items-center gap-3 rounded-xl p-3 border cursor-pointer ${pay === p.k ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`pay-${p.k}`}>
                <input type="radio" checked={pay === p.k} onChange={() => setPay(p.k)} className="accent-coral w-4 h-4" />
                <p.icon size={18} className="text-ink-600" />
                <span className="text-sm font-semibold">{p.l}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-ink-600 mt-2">Payments are verified server-side. {pay === "cod" ? "You'll pay in cash on delivery." : "Amount charged equals Food + Delivery − Discount."}</p>
        </div>

        <BillDetails cart={cart} title="Payment details" />
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-[520px] mx-auto bg-white border-t border-border p-4">
        <button onClick={placeOrder} disabled={busy} className="w-full bg-coral hover:bg-coral-hover text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 tap" data-testid="place-order-btn">
          {busy ? <Loader2 className="animate-spin" size={18} /> : `Place Order · ${inr(cart.total)}`}
        </button>
      </div>
    </div>
  );
}
